import Amplify from 'aws-amplify'
import { displayMessage } from '../helpers/helper.js'
import { i18n } from '@/i18n'

const DEFAULT_OPTIONS = {
  page: 1,
  itemsPerPage: 15,
  sortBy: 'list_id',
  sortDesc: '',
}

/**
 * Service for managing lists through HTTP API calls.
 * Provides methods to create, update, delete, fetch, and list data.
 */
export class ListService {
  /**
   * Creates an instance with the provided access token.
   * @param {string} accessToken - The access token used for authentication.
   */
  constructor(accessToken) {
    this.accessToken = accessToken
  }

  /**
   * Fetches a list by its ID from the API.
   * Displays an error message if the list is not found or if another error occurs.
   *
   * @param {string} listId - The ID of the list to retrieve.
   * @returns {Promise<Object|undefined>} The list data if found, otherwise undefined.
   */
  async get(listId) {
    const myInit = {
      headers: {
        Authorization: 'Bearer ' + this.accessToken,
      },
    }
    try {
      return await Amplify.API.get('locations', `/v1/int/lists/${listId}`, myInit)
    } catch (e) {
      console.log(e)
      if (e.response?.status === 404) {
        displayMessage(i18n.t('list.listNotFoundErrorMessage'), 'error')
      } else {
        displayMessage(i18n.t('list.listLoadingErrorMessage'), 'error')
      }
    }
  }

  /**
   * Updates a list by its ID with the provided data.
   * Displays a success or error message based on the result.
   *
   * @param {string} listId - The ID of the list to update.
   * @param {Object} data - The data to update the list with.
   * @returns {Promise<Object|undefined>} The updated list response, or undefined if an error occurred.
   */
  async update(listId, data) {
    const listCopy = this.generateListData(data)
    const myInit = {
      headers: {
        Authorization: 'Bearer ' + this.accessToken,
      },
      body: listCopy,
    }
    try {
      const res = await Amplify.API.put('locations', `/v1/int/lists/${listId}`, myInit)
      displayMessage(i18n.t('list.listUpdated'), 'success')
      return res
    } catch (e) {
      console.log(e)
      displayMessage(i18n.t('list.listUpdatingErrorMessage'), 'error')
    }
  }

  /**
   * Creates a new list with the provided data.
   * Displays a success or error message based on the result.
   *
   * @param {Object} data - The data for the new list.
   * @returns {Promise<Object|undefined>} The created list response, or undefined if an error occurred.
   */
  async create(data) {
    const listCopy = this.generateListData(data)
    const myInit = {
      headers: {
        Authorization: 'Bearer ' + this.accessToken,
      },
      body: listCopy,
    }
    try {
      const res = await Amplify.API.post('locations', '/v1/int/lists', myInit)
      displayMessage(i18n.t('list.listCreated'), 'success')
      return res
    } catch (e) {
      console.log(e)
      displayMessage(i18n.t('list.listCreatingErrorMessage'), 'error')
    }
  }

  /**
   * Deletes a list by its ID.
   * Displays a success or error message based on the result.
   *
   * @param {string} listId - The ID of the list to delete.
   * @returns {Promise<boolean>} True if deletion was successful, false otherwise.
   */
  async delete(listId) {
    const myInit = {
      headers: {
        Authorization: 'Bearer ' + this.accessToken,
      },
    }
    try {
      await Amplify.API.del('locations', `/v1/int/lists/${listId}`, myInit)
      displayMessage(i18n.t('list.listDeleteSuccessMessage'), 'success')
      return true
    } catch (e) {
      console.log(e)
      displayMessage(i18n.t('list.listDeletionErrorMessage'), 'error')
      return false
    }
  }

  /**
   * Retrieves a paginated list of lists based on query parameters.
   * Displays an error message if the request fails.
   *
   * @param {Object} queryStringParameters - Parameters for filtering and sorting.
   * @param {boolean} [pagination=true] - Whether to apply pagination.
   * @returns {Promise<{data: Object[], nbLists: number}|undefined>} The list data and total count, or undefined if an error occurred.
   */
  async list(queryStringParameters, pagination = true) {
    try {
      let myInit = this.buildMyInit(queryStringParameters, pagination)
      const res = await Amplify.API.get('locations', '/v1/int/lists', myInit)
      return { data: res.data, nbLists: res.nbLists }
    } catch {
      displayMessage(i18n.t('list.listsLoadingErrorMessage'), 'error')
    }
  }

  /**
   * Builds the initialization object for API requests, including headers and query parameters.
   *
   * @param {Object} [queryParams={}] - Query parameters for sorting and pagination.
   * @param {boolean} [pagination=true] - Whether to include pagination parameters.
   * @returns {Object} The initialization object for API requests.
   */
  buildMyInit(queryParams = {}, pagination = true) {
    let myInit = {
      headers: {
        Authorization: 'Bearer ' + this.accessToken,
      },
      queryStringParameters: {
        sortBy: queryParams.sortBy?.length > 0 ? queryParams.sortBy : DEFAULT_OPTIONS.sortBy,
        sortDesc: queryParams.sortDesc?.length > 0 ? queryParams.sortDesc : DEFAULT_OPTIONS.sortDesc,
        type: typeof queryParams.type === 'string' && queryParams.type.trim() ? queryParams.type.trim() : undefined,
      },
    }
    if (pagination) {
      myInit.queryStringParameters.page = queryParams?.page ? queryParams.page : DEFAULT_OPTIONS.page
      myInit.queryStringParameters.itemsPerPage = queryParams?.itemsPerPage
        ? queryParams.itemsPerPage
        : DEFAULT_OPTIONS.itemsPerPage
    } else {
      myInit.queryStringParameters.itemsPerPage = false
    }
    return myInit
  }

  /**
   * Executes a dynamic list by its ID, resolving conditions into location values.
   * Displays a success or error message based on the result.
   *
   * @param {string} listId - The ID of the list to execute.
   * @returns {Promise<Object|undefined>} The updated list response, or undefined if an error occurred.
   */
  async execute(listId) {
    const myInit = { headers: { Authorization: 'Bearer ' + this.accessToken } }
    let res
    try {
      res = await Amplify.API.post('locations', `/v1/int/lists/${listId}/execute`, myInit)
      displayMessage(i18n.t('list.executeSuccess'), 'success')
    } catch {
      displayMessage(i18n.t('list.executeErrorMessage'), 'error')
      res = await this.get(listId)
    }
    return res
  }

  /**
   * Transforms list data for API submission.
   * For DYNAMIC lists, removes the values field.
   * For STATIC lists, extracts only location IDs from values.
   *
   * @param {Object} data - The original list data.
   * @returns {Object} A copy of the list data prepared for the API.
   */
  generateListData(data) {
    let listCopy = structuredClone(data)
    if (listCopy.mode === 'DYNAMIC') {
      delete listCopy.values
      delete listCopy.last_execution_date
      delete listCopy.last_execution_status
      delete listCopy.last_execution_message
      delete listCopy.last_value_update
    } else {
      listCopy.values = listCopy.values.map((location) => location.location_id)
    }
    return listCopy
  }
}
